import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

export const THEMES = {
  light: {
    id: 'light',
    name: 'Clair',
    icon: '☀️',
    description: 'Style professionnel et épuré'
  },
  dark: {
    id: 'dark',
    name: 'Sombre',
    icon: '🌙',
    description: 'Style premium et luxueux'
  }
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('light');
  const [zenMode, setZenMode] = useState(false);

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('odys_theme');
    const theme = savedTheme && THEMES[savedTheme] ? savedTheme : 'light';
    setCurrentTheme(theme);
    applyTheme(theme);
    
    const savedZenMode = localStorage.getItem('odys_zen_mode') === 'true';
    setZenMode(savedZenMode);
  }, []);

  // Apply theme by setting class on document
  const applyTheme = useCallback((themeId) => {
    const root = document.documentElement;
    
    // Remove both classes first
    root.classList.remove('light', 'dark');
    
    // Add the appropriate class
    root.classList.add(themeId);
    
    // Also set color-scheme for native elements
    root.style.colorScheme = themeId;
    
    console.log(`Theme applied: ${themeId}`, root.classList.toString());
  }, []);

  // Change theme
  const changeTheme = useCallback((themeId) => {
    if (THEMES[themeId]) {
      setCurrentTheme(themeId);
      localStorage.setItem('odys_theme', themeId);
      applyTheme(themeId);
    }
  }, [applyTheme]);

  // Toggle zen mode
  const toggleZenMode = useCallback(() => {
    setZenMode(prev => {
      const newValue = !prev;
      localStorage.setItem('odys_zen_mode', newValue.toString());
      return newValue;
    });
  }, []);

  // Toggle between light and dark themes
  const toggleTheme = useCallback(() => {
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    changeTheme(newTheme);
  }, [currentTheme, changeTheme]);

  // Get current theme object
  const theme = THEMES[currentTheme] || THEMES.light;

  return (
    <ThemeContext.Provider value={{
      currentTheme,
      theme,
      themes: THEMES,
      changeTheme,
      toggleTheme,
      zenMode,
      toggleZenMode,
      isDark: currentTheme === 'dark',
      isLight: currentTheme === 'light'
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export default ThemeProvider;
